import { useFilters } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { competitionOptionsAtom, selectedCompetitionsAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonOptionsAtom, selectedSeasonsAtom } from '@/atoms/filters/highLevel/seasons';
import {
  comparisonTeamCompetitionOptionsAtom,
  comparisonTeamSeasonOptionsAtom,
  comparisonTeamSelectedCompetitionsAtom,
  comparisonTeamSelectedSeasonsAtom,
} from '@/atoms/filters/team/teamComparisonFilters';
import { filteredTeamGamesAtom, teamSelectedGamesAtom } from '@/atoms/filters/team/teamGames';
import { comparisonTeamCyclesOrderedAtom } from '@/atoms/queries/team/teamComparisonCycles';
import {
  comparisonTeamFilteredGamesAtom,
  comparisonTeamSelectedGamesAtom,
} from '@/atoms/queries/team/teamComparisonGames';
import { teamCyclesOrderedAtom } from '@/atoms/queries/teams';
import { useGetFilteredOptions } from '@/hooks/useGetFilteredOptions';
import { GamesFilterType } from '@/hooks/useGetGamesByFilter';
import { getDistinctOrderedCompetitionOptions } from '@/utils/cycles';

// When a competition or season filter is cleared (ids = []), the bio must show all available options for the entity —
// not just those that survive the remaining active game filters. These unwrapped atoms read directly from cycle-derived
// option lists, bypassing the game filter pipeline, to get the complete list
const allPrimaryCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev || []);
const allPrimarySeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev || []);
const allComparisonCompetitionOptionsAtom = unwrap(comparisonTeamCompetitionOptionsAtom, prev => prev || []);
const allComparisonSeasonOptionsAtom = unwrap(comparisonTeamSeasonOptionsAtom, prev => prev || []);

/**
 * Resolves which options to display for a filter bio row.
 * - null ids = untouched (show defaults from the active cycle)
 * - non-empty ids = user has made a selection (show that selection)
 * - empty ids [] = user cleared the filter (show everything available)
 */
const resolveFilterDisplayOptions = <T>(
  selectedIds: number[] | null,
  selectedOptions: T[],
  defaultOptions: T[],
  allOptions: T[],
): T[] => {
  if (selectedIds === null) return defaultOptions;
  return selectedIds.length ? selectedOptions : allOptions;
};

export const useTeamFilterDisplayOptions = () => {
  const teamCyclesOrdered = useAtomValue(teamCyclesOrderedAtom);
  const [{ competitionIds: selectedCompetitionIds, seasonIds: selectedSeasonIds }] = useFilters('main');
  const selectedCompetitions = useAtomValue(selectedCompetitionsAtom);
  const selectedSeasons = useAtomValue(selectedSeasonsAtom);
  const allCompetitionOptions = useAtomValue(allPrimaryCompetitionOptionsAtom);
  const allSeasonOptions = useAtomValue(allPrimarySeasonOptionsAtom);

  const filteredCompetitionOptions = useGetFilteredOptions(
    competitionOptionsAtom,
    filteredTeamGamesAtom,
    teamSelectedGamesAtom,
    GamesFilterType.COMPETITION,
  );
  const sortedCompetitionOptions = getDistinctOrderedCompetitionOptions(teamCyclesOrdered, filteredCompetitionOptions);
  const allSortedCompetitionOptions = getDistinctOrderedCompetitionOptions(teamCyclesOrdered, allCompetitionOptions);
  const competitionsToDisplay = resolveFilterDisplayOptions(
    selectedCompetitionIds,
    selectedCompetitions,
    sortedCompetitionOptions,
    allSortedCompetitionOptions,
  );

  const filteredSeasonOptions = useGetFilteredOptions(
    seasonOptionsAtom,
    filteredTeamGamesAtom,
    teamSelectedGamesAtom,
    GamesFilterType.SEASON,
  );
  const seasonsToDisplay = resolveFilterDisplayOptions(
    selectedSeasonIds,
    selectedSeasons,
    filteredSeasonOptions,
    allSeasonOptions,
  );

  return { competitionsToDisplay, seasonsToDisplay };
};

export const useComparisonTeamFilterDisplayOptions = () => {
  const teamCyclesOrdered = useAtomValue(comparisonTeamCyclesOrderedAtom);
  const selectedCompetitions = useAtomValue(comparisonTeamSelectedCompetitionsAtom);
  const selectedSeasons = useAtomValue(comparisonTeamSelectedSeasonsAtom);
  const [comparisonFilters] = useFilters('comparison');
  const selectedCompetitionIds = comparisonFilters.competitionIds;
  const selectedSeasonIds = comparisonFilters.seasonIds;
  const allCompetitionOptions = useAtomValue(allComparisonCompetitionOptionsAtom);
  const allSeasonOptions = useAtomValue(allComparisonSeasonOptionsAtom);

  const filteredCompetitionOptions = useGetFilteredOptions(
    comparisonTeamCompetitionOptionsAtom,
    comparisonTeamFilteredGamesAtom,
    comparisonTeamSelectedGamesAtom,
    GamesFilterType.COMPETITION,
  );
  const sortedCompetitionOptions = getDistinctOrderedCompetitionOptions(teamCyclesOrdered, filteredCompetitionOptions);
  const allSortedCompetitionOptions = getDistinctOrderedCompetitionOptions(teamCyclesOrdered, allCompetitionOptions);
  const competitionsToDisplay = resolveFilterDisplayOptions(
    selectedCompetitionIds,
    selectedCompetitions,
    sortedCompetitionOptions,
    allSortedCompetitionOptions,
  );

  const filteredSeasonOptions = useGetFilteredOptions(
    comparisonTeamSeasonOptionsAtom,
    comparisonTeamFilteredGamesAtom,
    comparisonTeamSelectedGamesAtom,
    GamesFilterType.SEASON,
  );
  const seasonsToDisplay = resolveFilterDisplayOptions(
    selectedSeasonIds,
    selectedSeasons,
    filteredSeasonOptions,
    allSeasonOptions,
  );

  return { competitionsToDisplay, seasonsToDisplay };
};
